import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Clock, Calendar as CalendarIcon, Lock } from 'lucide-react';
import { toast } from 'sonner';
import TierPaymentModal from '@/components/TierPaymentModal';
import TruncatedText from '@/components/TruncatedText';
import AddToCalendarButton from '@/components/AddToCalendarButton';
import { convertEventTime, getUserTimezone, sortEventsByTime } from '@/utils/timezoneUtils';

const DayEventsPage = ({ user }) => {
  const { date } = useParams(); // Format: YYYY-MM-DD
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membershipType, setMembershipType] = useState(user?.membership_tier || null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const fetchMembershipStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembershipType(response.data.membership_type);
    } catch (error) {
      console.error('Failed to fetch membership status');
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/events?per_page=500`);
      // Endpoint returns { events: [...] } since pagination was added
      const all = response.data.events || response.data || [];
      // Filter events for the selected date and sort by time
      const dayEvents = sortEventsByTime(
        all.filter(event => event.event_date === date)
      );
      setEvents(dayEvents);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchEvents();
    fetchMembershipStatus();
  }, [fetchEvents, fetchMembershipStatus]);

  const isViewOnly = membershipType !== null && membershipType === 'free';

  // Process events with timezone conversion
  const processedEvents = useMemo(() => {
    const userTimezone = getUserTimezone();
    
    return events.map(event => {
      const eventTimezone = event.timezone || 'America/New_York';
      const converted = convertEventTime(
        event.event_date,
        event.event_time,
        eventTimezone,
        userTimezone
      );
      
      return {
        ...event,
        displayTime: converted.displayTime,
        fullTimeDisplay: converted.fullDisplay,
        convertedDate: converted.date
      };
    });
  }, [events]);

  const formatDisplayDate = () => {
    if (!date) return '';
    const [year, month, day] = date.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return <div className="text-center py-20">Loading events...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Back to Calendar Button */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard/calendar')}
          className="bg-white hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Calendar
        </Button>
      </div>

      {/* Date Header */}
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">
          {formatDisplayDate()}
        </h1>
        <p className="text-lg text-gray-300">
          {events.length} {events.length === 1 ? 'event' : 'events'} scheduled
        </p>
      </div>

      {/* Membership Required Banner for Free Users */}
      {isViewOnly && (
        <Card 
          className="mb-6 border-2 border-yellow-500 bg-yellow-50 cursor-pointer hover:border-yellow-600 transition-all"
          onClick={() => setShowPaymentModal(true)}
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-900">View-Only Mode - Click to Upgrade</h3>
                <p className="text-sm text-yellow-800">
                  Upgrade your membership to access live sessions and interactive features.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Events List */}
      {processedEvents.length > 0 ? (
        <div className="space-y-4">
          {processedEvents.map((event) => {
            const [year, month, day] = event.event_date.split('-').map(Number);
            const eventDate = new Date(year, month - 1, day);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isPast = eventDate < today;
            
            return (
              <Card 
                key={event.id} 
                data-testid={`event-${event.id}`}
                className={`hover-lift border-l-4 bg-white ${
                  isPast ? 'border-gray-300 opacity-60' : 'border-cyan-400'
                }`}
                style={{ opacity: 1 }}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-shrink-0 text-center">
                      <div className="w-16 h-16 rounded-lg bg-white border-2 border-cyan-400 flex flex-col items-center justify-center">
                        <div className="text-2xl font-bold text-cyan-500">
                          {eventDate.getDate()}
                        </div>
                        <div className="text-xs uppercase text-cyan-500">
                          {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                        {isPast && <Badge variant="secondary">Past</Badge>}
                      </div>
                      <p className="text-gray-600 mb-3">
                        <TruncatedText 
                          text={event.description?.split('\n\nJoin URL:')[0] || ''} 
                          maxLength={250} 
                        />
                      </p>
                      <div className="flex items-center gap-4 text-sm flex-wrap mb-3">
                        <div className="flex items-center gap-1 text-cyan-500">
                          <Clock className="w-4 h-4" />
                          <span>{event.fullTimeDisplay}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <AddToCalendarButton event={event} variant="outline" size="sm" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-white" style={{ opacity: 1 }}>
          <CardContent className="py-12 text-center text-gray-500">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No events scheduled for this day.</p>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/calendar')}
              className="mt-4"
            >
              View Full Calendar
            </Button>
          </CardContent>
        </Card>
      )}

      <TierPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currentTier={user?.membership_tier || 'bronze'}
      />
    </div>
  );
};

export default DayEventsPage;
